import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faCode,
  faBuilding,
  faCheckCircle,
  faServer,
  faCloud,
  faArrowRight,
  faComments,
  faTimes,
  faSpinner,
  faMobile,
} from "@fortawesome/free-solid-svg-icons";
import {
  faAws,
  faReact,
  faGolang,
  faPython,
  faApple,
  faAndroid,
} from "@fortawesome/free-brands-svg-icons";
import AlertDialog from "./AlertDialog";
import MagneticButton from "./motion/MagneticButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { canUseCinematicMotion, gsap } from "../lib/motion";

const Packages: React.FC = () => {
  const location = useLocation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  useScrollReveal(sectionRef);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    description: "",
  });
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Open consultation modal from Header / Hero (#consult or ?consult=1)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsConsult =
      params.get("consult") === "1" || location.hash === "#consult";
    if (!wantsConsult) return;

    setSubject("Consultation");
    setIsModalOpen(true);

    const url = new URL(window.location.href);
    url.searchParams.delete("consult");
    url.hash = "";
    window.history.replaceState({}, "", url.pathname + url.search);
  }, [location.hash, location.pathname]);

  // Stagger package cards
  useEffect(() => {
    if (!cardsRef.current || !canUseCinematicMotion()) return;
    const cards = cardsRef.current.querySelectorAll("[data-package-card]");
    if (!cards.length) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        cards,
        { y: 36, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.75,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: cardsRef.current,
            start: "top 80%",
          },
        }
      );
    }, cardsRef);

    return () => ctx.revert();
  }, []);

  const openModal = (type: string, planName?: string) => {
    if (type === "consultation") {
      setSubject("Consultation");
    } else if (type === "started" && planName) {
      setSubject(`Development (${planName})`);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      description: "",
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);

    const apiBaseUrl =
      import.meta.env.VITE_API_BASE_URL || "http://dev-api.evently.ph";

    try {
      const response = await fetch(`${apiBaseUrl}/v1/contact/web-dev-package`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || "",
          subject: subject,
          description: formData.description,
        }),
      });

      if (response.ok) {
        setAlertDialog({
          isOpen: true,
          type: "success",
          title: "Message Sent!",
          message:
            "Thank you! Your message has been sent successfully. I'll get back to you soon.",
        });
        closeModal();
      } else {
        const errorData = await response.json();
        setAlertDialog({
          isOpen: true,
          type: "error",
          title: "Failed to Send",
          message:
            errorData.message || "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setAlertDialog({
        isOpen: true,
        type: "error",
        title: "Connection Error",
        message:
          "An error occurred. Please check your connection and try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const packages = [
    {
      title: "Starter",
      icon: faRocket,
      price: "$899+",
      description:
        "Perfect for personal brands and landing pages that need a professional edge.",
      tech: ["React / Vite", "Responsive Design", "Basic SEO"],
      features: [
        "Up to 5 Pages",
        "Contact Form Integration",
        "Speed Optimization",
        "Social Media Integration",
        "1 Month Support",
      ],
      color: "from-cyan-500 to-blue-500",
      buttonColor: "bg-cyan-600 hover:bg-cyan-700",
      techIcons: [faReact],
    },
    {
      title: "Professional",
      icon: faCode,
      price: "$2,500+",
      description:
        "Full-stack dynamic applications with robust backends and database integration.",
      recommended: true,
      tech: [
        "React Frontend",
        "Golang or Python Backend",
        "PostgreSQL / MySQL",
        "RESTful API",
      ],
      features: [
        "User Authentication",
        "Custom Admin Dashboard",
        "Database Integration",
        "Advanced SEO Setup",
        "3 Months Support",
        "API Integration",
      ],
      color: "from-indigo-500 to-purple-600",
      buttonColor: "bg-indigo-600 hover:bg-indigo-700",
      techIcons: [faReact, faGolang, faPython],
    },
    {
      title: "Enterprise",
      icon: faBuilding,
      price: "Custom",
      description:
        "Scalable enterprise systems built for complex business needs, high traffic, and mission-critical operations.",
      tech: [
        "Microservices Architecture",
        "Real-time Features (WebSockets)",
        "AWS Advanced Setup",
        "Third-party Integrations",
      ],
      features: [
        "Custom Business Logic & Workflows",
        "Multi-tenant Architecture",
        "Advanced Analytics & Reporting",
        "Enterprise-grade Security",
        "Priority 24/7 Support",
        "Performance Monitoring & Scaling",
      ],
      color: "from-violet-600 to-fuchsia-600",
      buttonColor: "bg-violet-600 hover:bg-violet-700",
      techIcons: [faReact, faGolang, faAws],
    },
  ];

  const mobilePackages = [
    {
      title: "MVP Starter",
      icon: faRocket,
      price: "$2,500+",
      description:
        "Launch your mobile app idea on both iOS and Android with a robust cross-platform MVP.",
      tech: ["React Native", "Expo", "Cross-Platform"],
      features: [
        "iOS & Android Compatible",
        "Standard UI/UX Design",
        "User Authentication",
        "Basic Database Setup",
        "App Store Submission Support",
      ],
      color: "from-emerald-500 to-teal-600",
      buttonColor: "bg-emerald-600 hover:bg-emerald-700",
      techIcons: [faReact, faApple, faAndroid],
    },
    {
      title: "Pro Application",
      icon: faMobile,
      price: "$4,500+",
      description:
        "Feature-rich mobile application tailored for growth, user engagement, and performance.",
      recommended: true,
      tech: [
        "React Native CLI",
        "Push Notifications",
        "Native Modules",
        "Real-time Data",
      ],
      features: [
        "Custom Advanced UI/UX",
        "Push Notification System",
        "Advanced State Management",
        "Offline Functionality",
        "3 Months Support",
        "Social Media Integration",
      ],
      color: "from-blue-600 to-cyan-600",
      buttonColor: "bg-blue-600 hover:bg-blue-700",
      techIcons: [faReact, faApple, faAndroid],
    },
    {
      title: "Enterprise Mobile",
      icon: faBuilding,
      price: "Custom",
      description:
        "Scalable, secure, and mission-critical mobile solutions for large organizations.",
      tech: [
        "Microservices",
        "CI/CD Pipelines",
        "Advanced Security",
        "Analytics",
      ],
      features: [
        "Complex Backend Integration",
        "Automated Testing & CI/CD",
        "Enterprise-grade Security",
        "Performance Optimization",
        "Priority 24/7 Support",
        "Dedicated Project Manager",
      ],
      color: "from-indigo-600 to-violet-600",
      buttonColor: "bg-indigo-600 hover:bg-indigo-700",
      techIcons: [faReact, faAws],
    },
  ];

  const hostingPlans = [
    {
      title: "Managed Hosting",
      description:
        "Focus on your business while I handle the technical infrastructure, security, and updates.",
      price: "From $29/mo",
      items: [
        "Static sites: $29/mo",
        "Apps with API + database: $99/mo",
        "Production-grade (high availability): $149/mo+",
        "Hosted on my AWS Infrastructure",
        "Automatic SSL (HTTPS) setup",
        "Daily Database Backups (7-day retention)",
        "Automated Security Alerts",
        "Regular Maintenance & Updates",
        "Fair-use traffic & storage; overage billed at cost",
      ],
      icon: faServer,
      tag: "Recommended for Non-Techies",
    },
    {
      title: "Developer Setup (Self-Hosted)",
      description:
        "I'll help you set up and configure everything on your preferred hosting platform (AWS, GoDaddy, DigitalOcean, etc.).",
      price: "$600 One-time",
      items: [
        "Setup on your Platform (AWS, GoDaddy, etc.)",
        "Environment Configuration",
        "Deployment Pipeline (CI/CD)",
        "Full Transfer of Ownership",
        "Architecture & Handover Documentation",
      ],
      icon: faCloud,
      tag: "Full Control",
    },
  ];

  return (
    <div
      ref={sectionRef}
      id="packages"
      className="min-h-screen pt-14 mt-10 pb-12 bg-[#0a0a0a] rounded-xl shadow-lg"
    >
      <div className="container mx-auto md:px-10 px-6">
        {/* Header Section */}
        <div className="text-center mb-16" data-reveal>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Web Development{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-500">
              Packages
            </span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Scalable, high-performance web solutions tailored to your business
            needs. Leveraging modern stacks like React, Angular, Golang, and
            Python.
          </p>
          <MagneticButton
            className="mt-6 px-6 py-3 rounded-xl bg-cyan-500 text-slate-950 font-semibold hover:bg-cyan-400 transition-colors"
            onClick={() => openModal("consultation")}
          >
            Book a free consult
          </MagneticButton>
        </div>

        {/* Pricing Cards Grid */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
        >
          {packages.map((pkg, index) => (
            <div
              key={index}
              data-package-card
              className={`relative group rounded-2xl p-px bg-gradient-to-b ${pkg.color} bg-opacity-20 hover:scale-[1.02] hover:-translate-y-1 transition-transform duration-300`}
            >
              <div className="bg-[#111111] rounded-2xl p-8 h-full flex flex-col border border-white/5">
                {pkg.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center mb-4`}
                  >
                    <FontAwesomeIcon
                      icon={pkg.icon}
                      className="text-white text-xl"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {pkg.title}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-white">
                      {pkg.price}
                    </span>
                    {pkg.price.includes("+") && (
                      <span className="text-gray-500 text-sm">starting at</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mb-6">
                    {pkg.description}
                  </p>
                </div>

                {/* <div className="mb-8">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
                    Technologies
                  </div>
                  <div className="flex items-center gap-4 mb-3">
                    {pkg.techIcons?.map((icon, i) => (
                      <FontAwesomeIcon
                        key={i}
                        icon={icon}
                        className="text-gray-400 text-xl hover:text-cyan-400 transition-colors"
                      />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pkg.tech.map((t, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-cyan-400 font-medium italic"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div> */}

                <div className="flex-grow">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
                    Included Features
                  </div>
                  <ul className="space-y-3">
                    {pkg.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-gray-300 text-sm"
                      >
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="text-cyan-500 mt-1"
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => openModal("started", pkg.title)}
                  className={`w-full mt-8 py-3 rounded-xl font-bold text-white transition-all duration-300 ${pkg.buttonColor} flex items-center justify-center gap-2 group`}
                >
                  Get Started
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Development Section */}
        <div className="text-center mb-16 mt-32 animate-fade-in">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Mobile App{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-500">
              Development
            </span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Native-like performance with the efficiency of cross-platform
            development. Building for both iOS and Android simultaneously.
          </p>
        </div>

        {/* Mobile Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {mobilePackages.map((pkg, index) => (
            <div
              key={index}
              data-package-card
              className={`relative group rounded-2xl p-px bg-gradient-to-b ${pkg.color} bg-opacity-20 hover:scale-[1.02] hover:-translate-y-1 transition-transform duration-300`}
            >
              <div className="bg-[#111111] rounded-2xl p-8 h-full flex flex-col border border-white/5">
                {pkg.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center mb-4`}
                  >
                    <FontAwesomeIcon
                      icon={pkg.icon}
                      className="text-white text-xl"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {pkg.title}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-white">
                      {pkg.price}
                    </span>
                    {pkg.price.includes("+") && (
                      <span className="text-gray-500 text-sm">starting at</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mb-6">
                    {pkg.description}
                  </p>
                </div>

                <div className="flex-grow">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
                    Included Features
                  </div>
                  <ul className="space-y-3">
                    {pkg.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-gray-300 text-sm"
                      >
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="text-emerald-500 mt-1"
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => openModal("started", "Mobile " + pkg.title)}
                  className={`w-full mt-8 py-3 rounded-xl font-bold text-white transition-all duration-300 ${pkg.buttonColor} flex items-center justify-center gap-2 group`}
                >
                  Get Started
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Hosting Options Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Infrastructure & Hosting
            </h2>
            <p className="text-gray-400">
              Expert setup and management across <strong>AWS</strong>,{" "}
              <strong>GoDaddy</strong>, <strong>DigitalOcean</strong>, and other
              major platforms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {hostingPlans.map((plan, index) => (
              <div
                key={index}
                className="bg-[#111111] border border-white/5 rounded-2xl p-8 hover:border-cyan-500/30 transition-colors"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={plan.icon}
                      className="text-cyan-400 text-xl"
                    />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white">
                      {plan.title}
                    </h4>
                    <span className="text-xs text-cyan-500 font-medium uppercase tracking-wider">
                      {plan.tag}
                    </span>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-6">{plan.description}</p>
                <div className="text-2xl font-bold text-white mb-6">
                  {plan.price}
                </div>
                <ul className="space-y-3">
                  {plan.items.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 text-gray-300 text-sm"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Free Consultation Section */}
        <div className="mt-24 mb-12">
          <div className="relative overflow-hidden rounded-3xl p-8 md:p-12 bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-white/5 text-center">
            <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/5 mb-6">
                <FontAwesomeIcon
                  icon={faComments}
                  className="text-cyan-400 text-2xl"
                />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Not sure where to start?
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Every great project begins with a conversation. I offer a{" "}
                <strong className="text-white">
                  free 30-minute consultation
                </strong>{" "}
                to discuss your goals, technical requirements, and how we can
                bring your vision to life.
              </p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => openModal("consultation")}
                  className="w-full md:w-auto px-8 py-4 bg-white text-black font-bold rounded-xl hover:bg-cyan-400 hover:text-white transition-all duration-300"
                >
                  Book Free Call
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-20 text-center text-gray-500 text-sm">
          <p>
            Need a custom solution not listed here?
            <button
              onClick={() => openModal("consultation")}
              className="text-cyan-500 hover:underline ml-1 bg-transparent border-none p-0"
            >
              Let's discuss your project.
            </button>
          </p>
        </div>
      </div>

      {/* Contact Form Dialog */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div
            className="bg-[#111111] border border-white/10 w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {subject.startsWith("Consultation")
                      ? "Book Consultation"
                      : "Project Inquiry"}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">{subject}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-all"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                    Name / Company *
                  </label>
                  <input
                    required
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe / Acme Inc."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-cyan-500/50 focus:bg-white/10 transition-all outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                      Email *
                    </label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-cyan-500/50 focus:bg-white/10 transition-all outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-cyan-500/50 focus:bg-white/10 transition-all outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                    Description *
                  </label>
                  <textarea
                    required
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Tell me about your project or what you'd like to discuss..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-cyan-500/50 focus:bg-white/10 transition-all outline-none resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 bg-gradient-to-r from-cyan-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-cyan-900/20 transition-all duration-300 mt-2 flex items-center justify-center gap-2 ${
                    isSubmitting
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:from-cyan-500 hover:to-indigo-500"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin"
                        aria-hidden="true"
                      />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
      />
    </div>
  );
};

export default Packages;
